import { useElements, useStripe } from '@stripe/react-stripe-js';
import { message } from 'antd';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo } from 'react';

import { CardVCC } from 'app/components/commons/Cards/CardVCC';
import type { VCC_STATUS } from 'app/typings/core/common/enum/vccStatus';
import { VccStatus } from 'app/typings/vcc';

import './VirtualCard.scss';
import type { AlertConfig } from './_components/CardAlerts';
import { CardAlerts } from './_components/CardAlerts';
import { CardDetails } from './_components/CardDetail';
import { CardHistory } from './_components/CardHistory';
import { CardStatus } from './_components/CardStatus';
import type { VirtualCardConfig, VirtualCardData } from './types';

export type VirtualCardStripeConfig = {
  getEphemeralKey: (
    nonce: string,
    cardId: string
  ) => Promise<{ secret: string }>;
};

type VirtualCardProps = {
  data: VirtualCardData;
  config: VirtualCardConfig;
  stripeConfig: VirtualCardStripeConfig;
  alertConfig?: AlertConfig;
  getTooltipText?: (status: VccStatus | VCC_STATUS) => string | null;
  cardVisualizer?: ReactNode;
};

export const VirtualCard = ({
  data,
  config,
  stripeConfig,
  alertConfig,
  getTooltipText,
  cardVisualizer,
}: VirtualCardProps) => {
  const status = config.status;
  const cardIsCanceled = status.status === VccStatus.Cancelled;
  const cardIsDeactivated = status.status === VccStatus.Deactivated;
  const cardIsBlur = cardIsCanceled || cardIsDeactivated;

  const stripe = useStripe();
  const elements = useElements();

  const baseStyle = useMemo(
    () => ({
      color: '#fff',
      fontFamily: 'system-ui, "Helvetica Neue", sans-serif',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 500,
    }),
    []
  );

  const alertCopyButton = useCallback(() => {
    message.success('Copy to clipboard!');
  }, []);

  useEffect(() => {
    const createdElements: { destroy: () => void }[] = [];

    async function setupStripeCard() {
      if (!stripe || !elements || !data.issuingCardId) {
        return;
      }

      const nonceResult = await stripe.createEphemeralKeyNonce({
        issuingCard: data.issuingCardId,
      });
      const nonce = nonceResult.nonce;

      if (!nonce) {
        return;
      }

      const ephemeralKey = (
        await stripeConfig.getEphemeralKey(nonce, data.issuingCardId)
      ).secret;

      const cardElement = elements?.create('issuingCardNumberDisplay', {
        issuingCard: data.issuingCardId,
        nonce: nonce,
        ephemeralKeySecret: ephemeralKey,
        style: {
          base: {
            ...baseStyle,
            fontSize: '18px',
            fontWeight: 700,
          },
        },
      });

      const cardCvc = elements?.create('issuingCardCvcDisplay', {
        issuingCard: data.issuingCardId,
        nonce: nonce,
        ephemeralKeySecret: ephemeralKey,
        style: {
          base: baseStyle,
        },
      });

      const cardExpiry = elements?.create('issuingCardExpiryDisplay', {
        issuingCard: data.issuingCardId,
        nonce: nonce,
        ephemeralKeySecret: ephemeralKey,
        style: {
          base: baseStyle,
        },
      });

      const cardCopyButton = elements.create('issuingCardCopyButton', {
        toCopy: 'number',
        style: {
          base: { ...baseStyle, padding: '7px' },
        },
      });

      const cardPin = elements?.create('issuingCardPinDisplay', {
        issuingCard: data.issuingCardId,
        nonce: nonce,
        ephemeralKeySecret: ephemeralKey,
        style: {
          base: {
            ...baseStyle,
            color: '#262626',
          },
        },
      });

      createdElements.push(
        cardElement,
        cardCvc,
        cardExpiry,
        cardCopyButton,
        cardPin
      );

      cardCvc.mount('#card-cvc');
      cardElement.mount('#card-number');
      cardExpiry.mount('#card-expiry');
      cardPin.mount('#card-pin');
      cardCopyButton.mount('#card-number-copy');

      cardCopyButton.on('click', () => {
        alertCopyButton();
      });
    }

    setupStripeCard();

    return () => {
      createdElements.forEach((element) => element.destroy());
    };
  }, [
    baseStyle,
    elements,
    stripeConfig,
    stripe,
    data.issuingCardId,
    alertCopyButton,
  ]);

  return (
    <div className="virtual-card-container">
      <div className="card-info">
        <CardStatus status={status} tooltipTextFn={getTooltipText} />

        {!cardIsCanceled ? (
          <>
            <CardDetails data={data} config={config} />
            <CardHistory status={status} />
          </>
        ) : (
          <></>
        )}
      </div>
      <div className="card-visualizer">
        {cardVisualizer ?? <CardVCC isBlur={cardIsBlur} />}
      </div>
      <CardAlerts
        data={data}
        cardIsCanceled={cardIsCanceled}
        cardIsDeactivated={cardIsDeactivated}
        alertConfig={alertConfig}
      />
    </div>
  );
};
